import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { ItemResponse, ListResponse, PortalAssemblyDocument } from '@proerp/shared';
import { portalRequest, uploadPortalFile } from './apiClient';

export const PORTAL_DOCUMENTS_KEY = ['portal', 'assembly-documents'] as const;

/**
 * Unterlagen zu einem Montage-Einsatz (ADR 0087). Je Einsatz eine eigene
 * Abfrage — sie wird erst gestellt, wenn jemand den Einsatz aufklappt: Die
 * Liste hat oft viele Einsätze, und die wenigsten haben Unterlagen, die
 * gerade jemanden interessieren.
 */
export function usePortalDocuments(
  assemblyTaskId: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<PortalAssemblyDocument[]> {
  return useQuery({
    queryKey: [...PORTAL_DOCUMENTS_KEY, assemblyTaskId],
    queryFn: async () => {
      const response = await portalRequest<ListResponse<PortalAssemblyDocument>>(
        `/assembly/${assemblyTaskId}/documents`,
      );
      return response.data;
    },
    enabled: options.enabled ?? true,
    retry: false,
  });
}

/** Foto oder Nachweis von der Baustelle hochladen. */
export function usePortalDocumentUpload(assemblyTaskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) =>
      uploadPortalFile<ItemResponse<PortalAssemblyDocument>>(
        `/assembly/${assemblyTaskId}/documents?fileName=${encodeURIComponent(file.name)}`,
        file,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [...PORTAL_DOCUMENTS_KEY, assemblyTaskId] }),
  });
}
